import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store.tsx";
import {
	AllFieldsRequiredText,
	CancelButtonText,
	CantProcessRequestText,
	CatalogText,
	ContinueButtonText,
	CSVIncludesCustomColorMessage,
	DoorStyleText,
	FileNameText,
	MissingCustomColorAlertBannerMessage,
	MissingCustomColorAlertBannerTitle,
	OrderSampleButtonText,
	SelectCustomColorTitle,
	ServerTemporarilyUnavailableText
} from "constants/text.ts";
import { brandCheck } from "utils/order.ts";
import StyledSummaryCardLogo from "styles/StyledComponents/StyledSummaryCardLogo.tsx";
import { useDialog } from "hooks/useDialog.ts";
import {
	SelectCustomColorCatalogContent,
	SelectCustomColorCatalogContentColumn,
	SelectCustomColorCatalogContentWrapper,
	SelectCustomColorModalContinueButtons,
	SelectCustomColorModalErrorBanner,
	SelectCustomColorModalWrapper
} from "components/NewOrders/SelectCustomColorModal/SelectCustomColorModal.styles.ts";
import { models } from "types/api/viewModels.ts";
import { useLazyGetSpeciesDataQuery } from "features/api/globalsApi.ts";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import AutoComplete from "components/Common/Autocomplete/Autocomplete.tsx";
import { AlertTitle, Button } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import SelectCustomColorModalFormProvider from "components/NewOrders/SelectCustomColorModal/SelectCustomColorModalFormProvider.tsx";
import {
	FinishFields,
	SelectCustomColorModalSchema
} from "components/NewOrders/SelectCustomColorModal/SelectCustomColorModalSchema.ts";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import { fileNameSymbol } from "components/NewOrders/CSVUpload/CSVUpload.tsx";
import { useParseOrderMutation } from "features/api/newOrderApi.ts";
import { useLazyGetAccountsQuery } from "features/api/accountApi.ts";
import { useUserLocation } from "hooks/useUserLocation.ts";
import { doDispatches, fetchProductLineAccounts, formatFinishOption } from "../utils/NewOrderUtils.tsx";
import { LoadingContainer, LoadingText } from "../FixGlobalAttributesDialog/FixGlobalAttributesDialog.styles.ts";
import { FixCatalogGlobalAttributesLoadingText } from "../constants.ts";
import LinearProgress from "@mui/material/LinearProgress";
import { newOrderActions } from "features/reducers/newOrder/newOrder.ts";

export const SelectCustomColorModal = () => {
	const catalogsWithCustomFinish = useSelector(
		(state: RootState) => state.newOrder.catalogs?.catalogsWithCustomFinish
	);
	const [finishes, setFinishes] = useState<models["FinishViewModel"][]>([]);
	const [trigger, { isLoading, isUninitialized }] = useLazyGetSpeciesDataQuery();
	const noFinishesAvailable = !finishes.length && !isUninitialized && !isLoading;

	return (
		<SelectCustomColorModalFormProvider
			noFinishesAvailable={noFinishesAvailable}
			defaultValues={{ finishes: noFinishesAvailable ? null : [] }}
		>
			<SelectCustomColorModalContent
				finishes={finishes}
				setFinishes={setFinishes}
				trigger={trigger}
				isLoading={isLoading}
				noFinishesAvailable={noFinishesAvailable}
				catalogsWithCustomFinish={catalogsWithCustomFinish}
			/>
		</SelectCustomColorModalFormProvider>
	);
};

interface SelectCustomColorModalContentProps {
	finishes: models["FinishViewModel"][];
	setFinishes: Dispatch<SetStateAction<models["FinishViewModel"][]>>;
	trigger: any;
	noFinishesAvailable: boolean;
	isLoading: boolean;
	catalogsWithCustomFinish?: models["CatalogViewModel"][];
}

const SelectCustomColorModalContent = ({
	finishes,
	setFinishes,
	trigger,
	noFinishesAvailable,
	catalogsWithCustomFinish,
	isLoading
}: SelectCustomColorModalContentProps) => {
	const { closeDialog, openDialog } = useDialog();
	const catalogsWithoutCustomFinish = useSelector(
		(state: RootState) => state.newOrder.catalogs?.catalogsWithoutCustomFinish
	);
	const draftOrder = useSelector((state: RootState) => state.newOrder.draftDetails?.draftOrder);
	const { control, handleSubmit } = useFormContext<FinishFields>();
	const [parseOrder, { error: parsingError }] = useParseOrderMutation();
	const [getAccountByProductLine] = useLazyGetAccountsQuery();
	const [isParsing, setIsParsing] = useState<boolean>(false);
	const { isNewOrderBuildOrderPage, isAddOnBuildOrderPage } = useUserLocation();

	const dedupeFinishes = (finishes: models["FinishViewModel"][]) => {
		return finishes.filter(
			(finish, index, self) => self.findIndex((f) => JSON.stringify(f) === JSON.stringify(finish)) === index
		);
	};

	const dispatch = useDispatch();

	useEffect(() => {
		// Ensures that the modal does not appear if the page is refreshed
		const handleBeforeUnload = () => {
			closeDialog();
		};
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, [closeDialog]);

	const updateFinishes = useCallback(
		async (catalog?: models["CatalogViewModel"]) => {
			try {
				await Promise.resolve();
				const response: models["SpeciesViewModel"] = await trigger({
					productLineId: catalog?.globals?.productLine?.id ?? "",
					styleId: catalog?.globals?.style?.id ?? "",
					speciesId: catalog?.globals?.species?.id ?? "",
					customFinishes: false
				}).unwrap();
				setFinishes((prev) => {
					const finishes = (response?.finishes ?? []).filter(
						(finish: models["FinishViewModel"] | undefined | null): finish is models["FinishViewModel"] =>
							Boolean(finish && finish.customColorSampleOrderNumber)
					);
					const allFinishes = [...prev, ...finishes];
					return dedupeFinishes(allFinishes);
				});
			} catch (err) {
				console.error("Could not update finishes at this time", err);
			}
		},
		[setFinishes, trigger]
	);

	const onSave = async (values: SelectCustomColorModalSchema) => {
		let newCatalogs = [...(catalogsWithCustomFinish ?? [])];
		if (noFinishesAvailable) {
			dispatch(newOrderActions.clearNewOrder());
			openDialog({ dialogStatus: DialogTracking.CUSTOM_COLOR_ENTRY });
			return;
		}
		if (!newCatalogs) return;
		setIsParsing(true);
		values.finishes?.forEach((finish, index) => {
			newCatalogs[index] = {
				...newCatalogs[index],
				globals: {
					...newCatalogs[index]?.globals,
					finish: finish
				}
			};
		});

		const fixedByFile: Record<string, models["CatalogViewModel"][]> = {};
		for (const cat of newCatalogs) {
			const fileName = cat[fileNameSymbol];
			if (!fixedByFile[fileName!]) {
				fixedByFile[fileName!] = [];
			}
			fixedByFile[fileName!]!.push(cat);
		}

		let parsedCSVs: models["ValidatedOrderViewModel"][] = [];
		const fixedFileNames = Object.keys(fixedByFile);

		for (const fileName of fixedFileNames) {
			const prevValidForFile =
				catalogsWithoutCustomFinish?.filter((catalog) => catalog.fileName === fileName) || [];
			const merged = [...prevValidForFile, ...(fixedByFile[fileName] ?? [])];

			const importedCSVData = await parseOrder(merged).unwrap();
			parsedCSVs.push({ ...importedCSVData, fileName });
		}

		if (parsedCSVs.length > 0) {
			for (const csv of parsedCSVs) {
				doDispatches(csv, dispatch, isNewOrderBuildOrderPage, isAddOnBuildOrderPage, draftOrder);
				await fetchProductLineAccounts(csv, dispatch, getAccountByProductLine);
			}
		}

		if (parsedCSVs.length > 0) {
			openDialog({ dialogStatus: DialogTracking.SELECT_BILLTO_ACCOUNT });
			setIsParsing(false);
		}
	};

	useEffect(() => {
		if (catalogsWithCustomFinish?.length) {
			const fetchAllFinishes = async () => {
				await Promise.all(catalogsWithCustomFinish.map((catalog) => updateFinishes(catalog)));
			};
			void fetchAllFinishes();
		}
	}, [catalogsWithCustomFinish, updateFinishes]);

	return (
		<SelectCustomColorModalWrapper
			open
			onClose={() => closeDialog()}
		>
			<form
				noValidate
				onSubmit={handleSubmit(onSave)}
			>
				<h1>{SelectCustomColorTitle}</h1>
				<span>{AllFieldsRequiredText}</span>
				<span>{CSVIncludesCustomColorMessage}</span>
				{noFinishesAvailable && !isLoading && (
					<SelectCustomColorModalErrorBanner severity="error">
						<AlertTitle>{MissingCustomColorAlertBannerTitle}</AlertTitle>
						<p>{MissingCustomColorAlertBannerMessage}</p>
					</SelectCustomColorModalErrorBanner>
				)}
				{parsingError && !isLoading && (
					<SelectCustomColorModalErrorBanner severity="error">
						<AlertTitle>{CantProcessRequestText}</AlertTitle>
						<p>{ServerTemporarilyUnavailableText}</p>
					</SelectCustomColorModalErrorBanner>
				)}
				<SelectCustomColorCatalogContentWrapper>
					{catalogsWithCustomFinish?.map((catalog: models["CatalogViewModel"], index) => {
						const parentBrand = catalog.globals?.brand?.description;
						const productLine = catalog.globals?.productLine?.description;
						return (
							<div key={parentBrand}>
								{!isParsing && !isLoading && (
									<SelectCustomColorCatalogContent>
										<SelectCustomColorCatalogContentColumn width={172}>
											<StyledSummaryCardLogo
												data-testid="new-order-select-custom-color-logo"
												parentBrand={parentBrand}
												style={{
													height: "14px"
												}}
												src={
													brandCheck(parentBrand)
														? `/assets/manufacture_logos/${parentBrand}.png`
														: "/assets/tandem_logos/cwg_logo.png"
												}
												alt={
													parentBrand && brandCheck(parentBrand)
														? parentBrand
														: "CabinetworksGroup Logo"
												}
											/>
											<span>{productLine}</span>
										</SelectCustomColorCatalogContentColumn>
										<SelectCustomColorCatalogContentColumn width={172}>
											<span>{FileNameText}</span>
											<span>{catalog.fileName}</span>
										</SelectCustomColorCatalogContentColumn>
										<SelectCustomColorCatalogContentColumn width={82}>
											<span>{CatalogText}</span>
											<span>{catalog.name}</span>
										</SelectCustomColorCatalogContentColumn>
										<SelectCustomColorCatalogContentColumn width={79}>
											<span>{DoorStyleText}</span>
											<span>{catalog.globals?.style?.description}</span>
										</SelectCustomColorCatalogContentColumn>
									</SelectCustomColorCatalogContent>
								)}
								{!noFinishesAvailable && !isParsing && (
									<Controller
										control={control}
										name={`finishes.${index}`}
										render={({ field: { onChange, value = null }, fieldState: { error } }) => {
											return (
												<AutoComplete
													options={finishes}
													getOptionLabel={(
														option: models["FinishViewModel"] | null | undefined
													) => formatFinishOption(option)}
													onChange={(_, value) => onChange(value)}
													errorText={error?.message}
													label="Select Finish"
													dataTestId="select-custom-color-modal-finish"
													isLoading={isLoading}
													required
													value={value}
													isError={!!error}
												/>
											);
										}}
									/>
								)}
							</div>
						);
					})}
					{isParsing && (
						<LoadingContainer
							style={{ minHeight: 0 }}
							data-testid="custom-color-selection-loading-banner"
						>
							<LoadingText>{FixCatalogGlobalAttributesLoadingText}</LoadingText>
							<LinearProgress sx={{ width: "80%", minWidth: 200, maxWidth: 472 }} />
						</LoadingContainer>
					)}
					{!isParsing && !isLoading && (
						<SelectCustomColorModalContinueButtons>
							<Button
								variant="outlined"
								onClick={() => closeDialog()}
							>
								{CancelButtonText}
							</Button>
							<Button
								variant="contained"
								type="submit"
							>
								{noFinishesAvailable ? OrderSampleButtonText : ContinueButtonText}
							</Button>
						</SelectCustomColorModalContinueButtons>
					)}
				</SelectCustomColorCatalogContentWrapper>
			</form>
		</SelectCustomColorModalWrapper>
	);
};
